export function buildPcapCollapsedGraph(baseGraph) {
  const g = baseGraph || { nodes: [], links: [] };
  const nodes = Array.isArray(g.nodes) ? g.nodes : [];
  const links = Array.isArray(g.links) ? g.links : [];
  const map = new Map();

  const safeMac = (v) => {
    const s = (v == null) ? '' : String(v).trim();
    if (!s || s === '-') return '';
    return s;
  };

  const setMacForEndpoint = (obj, endpointKey, mac) => {
    const m = safeMac(mac);
    if (!m) return;
    if (endpointKey === 'a') {
      if (!safeMac(obj.macA)) obj.macA = m;
    } else if (endpointKey === 'b') {
      if (!safeMac(obj.macB)) obj.macB = m;
    }
  };

  links.forEach((l) => {
    if (!l) return;
    const s = typeof l.source === 'object' ? l.source.id : l.source;
    const t = typeof l.target === 'object' ? l.target.id : l.target;
    if (!s || !t) return;
    const a = String(s);
    const b = String(t);
    const ordered = [a, b].sort();
    const key = `${ordered[0]}↔${ordered[1]}`;
    const prev = map.get(key);
    const v = Number(l.value) || 0;
    const p = Number(l.packets) || 0;
    const d = Number(l.duration) || 0;
    const dir = (l.pcapDir || '').toString().toLowerCase();
    const txPart = (dir === 'tx') ? v : (Number(l.txBytes) || 0);
    const rxPart = (dir === 'rx') ? v : (Number(l.rxBytes) || 0);

    // Try to infer endpoint MACs from directed link metadata.
    const endpointKeyFor = (id) => (String(id) === String(ordered[0]) ? 'a' : 'b');
    const macFrom = safeMac(l.fromMac) || safeMac(l.macA) || '';
    const macTo = safeMac(l.toMac) || safeMac(l.macB) || '';

    if (prev) {
      prev.value += v;
      prev.packets = (prev.packets || 0) + p;
      prev.duration = Math.max(prev.duration || 0, d);
      prev.txBytes = (prev.txBytes || 0) + txPart;
      prev.rxBytes = (prev.rxBytes || 0) + rxPart;

      // Preserve a representative MAC for each endpoint.
      setMacForEndpoint(prev, endpointKeyFor(s), macFrom);
      setMacForEndpoint(prev, endpointKeyFor(t), macTo);

      try {
        const flows = l && l.pcapFlows && typeof l.pcapFlows === 'object' ? l.pcapFlows : null;
        if (flows) {
          if (!prev.pcapFlows) prev.pcapFlows = {};
          Object.keys(flows).forEach((k2) => {
            const f = flows[k2];
            if (!f) return;
            const existing = prev.pcapFlows[k2] || { clientPort: '-', serverPort: '-', clientBytes: 0, serverBytes: 0, packets: 0 };
            if (f.clientPort != null) existing.clientPort = f.clientPort;
            if (f.serverPort != null) existing.serverPort = f.serverPort;
            existing.clientBytes += Number(f.clientBytes) || 0;
            existing.serverBytes += Number(f.serverBytes) || 0;
            existing.packets += Number(f.packets) || 0;
            prev.pcapFlows[k2] = existing;
          });
        }
        if (!prev.ports && l && l.ports) prev.ports = l.ports;
      } catch (e) {
      }
    } else {
      const collapsed = {
        id: key,
        source: ordered[0],
        target: ordered[1],
        value: v,
        gas: 0,
        packets: p,
        duration: d,
        txBytes: txPart,
        rxBytes: rxPart,
        metaType: 'pcap',
        curvature: 0,
        pcapDir: 'both',
      };

      // Initialize endpoint MACs.
      setMacForEndpoint(collapsed, endpointKeyFor(s), macFrom);
      setMacForEndpoint(collapsed, endpointKeyFor(t), macTo);

      try {
        const flows = l && l.pcapFlows && typeof l.pcapFlows === 'object' ? l.pcapFlows : null;
        if (flows) {
          collapsed.pcapFlows = {};
          Object.keys(flows).forEach((k2) => {
            const f = flows[k2];
            if (!f) return;
            collapsed.pcapFlows[k2] = {
              clientPort: f.clientPort,
              serverPort: f.serverPort,
              clientBytes: Number(f.clientBytes) || 0,
              serverBytes: Number(f.serverBytes) || 0,
              packets: Number(f.packets) || 0,
            };
          });
        }
        if (l && l.ports) collapsed.ports = l.ports;
      } catch (e) {
      }

      map.set(key, collapsed);
    }
  });
  return { nodes, links: Array.from(map.values()) };
}

export function buildPcapGroupedGraph(baseGraph) {
  const g = baseGraph || { nodes: [], links: [] };
  const nodes = Array.isArray(g.nodes) ? g.nodes : [];
  const links = Array.isArray(g.links) ? g.links : [];

  const safeMac = (v) => {
    const s = (v == null) ? '' : String(v).trim();
    if (!s || s === '-') return '';
    return s;
  };

  const normalizeTextKey = (s) => String(s || '').trim().toLowerCase();

  const isEffectivelyUnnamedLabel = (labelKey, idKey) => {
    const lk = String(labelKey || '').trim();
    if (!lk) return true;
    if (lk === String(idKey || '').trim()) return true;
    if (lk === '-') return true;
    if (lk === 'n/a') return true;
    if (lk === 'na') return true;
    if (lk === 'unknown') return true;
    return false;
  };

  const hashColorHex = (str) => {
    const s = String(str || '');
    let h = 2166136261;
    for (let i = 0; i < s.length; i += 1) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    const hue = ((h >>> 0) % 360);
    return `hsl(${hue}, 75%, 55%)`;
  };

  const isIPv4 = (s) => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(String(s || '').trim());

  const opts = (arguments.length > 1 && arguments[1] && typeof arguments[1] === 'object') ? arguments[1] : {};
  const groupNames = !!opts.groupNames;
  const groupIps = !!opts.groupIps;
  const manualNameKeys = new Set(Array.isArray(opts.manualNameKeys) ? opts.manualNameKeys.map(normalizeTextKey) : []);
  const manualIpFirstOctets = new Set(Array.isArray(opts.manualIpFirstOctets) ? opts.manualIpFirstOctets.map((v) => String(v || '').trim()) : []);
  const ipBucketingOn = !!(groupIps || manualIpFirstOctets.size);

  const unnamedIpFirstOctetCounts = (() => {
    if (!ipBucketingOn) return new Map();
    const counts = new Map();
    for (let i = 0; i < nodes.length; i += 1) {
      const n = nodes[i];
      if (!n) continue;
      const id = n && n.id != null ? String(n.id).trim() : '';
      if (!isIPv4(id)) continue;
      const label = n && n.label != null ? String(n.label).trim() : '';
      const idKey = normalizeTextKey(id);
      const labelKey = normalizeTextKey(label);
      // If Group Names is OFF, Group IPs should still bucket by /8 even when
      // label metadata exists (e.g., public IP reverse-DNS / vendor strings).
      // If Group Names is ON, only bucket "raw" IP nodes that have no
      // meaningful label, so name-grouping can take precedence.
      const firstOctet = id.split('.')[0] || '';
      if (!firstOctet) continue;

      const eligibleForBucketing = (manualIpFirstOctets.size && manualIpFirstOctets.has(firstOctet))
        ? isEffectivelyUnnamedLabel(labelKey, idKey)
        : (groupIps ? (groupNames ? isEffectivelyUnnamedLabel(labelKey, idKey) : true) : false);
      if (!eligibleForBucketing) continue;
      counts.set(firstOctet, (counts.get(firstOctet) || 0) + 1);
    }
    return counts;
  })();

  const isMacAddress = (s) => {
    const raw = String(s || '').trim();
    if (!raw) return false;
    return /^([0-9A-Fa-f]{2}([:-])){5}[0-9A-Fa-f]{2}$/.test(raw);
  };

  const groupIdForNode = (node) => {
    const id = node && node.id != null ? String(node.id) : '';
    const label = node && node.label != null ? String(node.label) : '';

    if (isMacAddress(id) || isMacAddress(label)) {
      return { groupId: `mac:${normalizeTextKey(id)}`, display: id };
    }

    const labelKey = normalizeTextKey(label);
    const idKey = normalizeTextKey(id);

    if ((groupNames && labelKey && labelKey !== idKey) || (labelKey && manualNameKeys.has(labelKey))) {
      return { groupId: `name:${labelKey}`, display: label.trim() || label };
    }

    if (ipBucketingOn && isIPv4(id)) {
      const ip = id.trim();
      const firstOctet = ip.split('.')[0] || ip;
      const count = unnamedIpFirstOctetCounts.get(firstOctet) || 0;

      // If Group Names is OFF, always bucket by /8 when multiple entries exist.
      // If Group Names is ON, only bucket "raw" (effectively-unnamed) IP nodes
      // so name grouping remains the primary collapse mechanism.
      const eligibleForBucketing = (manualIpFirstOctets.size && manualIpFirstOctets.has(firstOctet))
        ? isEffectivelyUnnamedLabel(labelKey, idKey)
        : (groupIps ? (groupNames ? isEffectivelyUnnamedLabel(labelKey, idKey) : true) : false);
      if (eligibleForBucketing && (manualIpFirstOctets.has(firstOctet) || count > 1)) {
        const bucket = `${firstOctet}.0.0.0/8`;
        return { groupId: `ip:${normalizeTextKey(bucket)}`, display: bucket };
      }

      const useLabel = (labelKey && !isEffectivelyUnnamedLabel(labelKey, idKey) && labelKey !== idKey);
      const display = useLabel ? label.trim() : ip;
      return { groupId: `ip:${normalizeTextKey(ip)}`, display };
    }

    // Preserve meaningful labels for nodes that are not being grouped.
    // Otherwise, a first manual group action can wipe labels out (by forcing
    // label==id), preventing subsequent group-by-name actions.
    const useLabel = (labelKey && !isEffectivelyUnnamedLabel(labelKey, idKey) && labelKey !== idKey);
    const display = useLabel ? label.trim() : id;
    return { groupId: `id:${idKey}`, display };
  };

  const groupMap = new Map();
  const groupSums = new Map();
  const nodeToGroup = new Map();

  nodes.forEach((n) => {
    if (!n) return;
    const { groupId, display } = groupIdForNode(n);
    nodeToGroup.set(String(n.id), groupId);
    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, {
        id: groupId,
        label: display,
        symbol: display,
        displayId: display,
        metaType: 'pcap',
        group: n.group,
        color: hashColorHex(display),
        icon: n && n.icon ? n.icon : undefined,
      });
    } else {
      const existing = groupMap.get(groupId);
      if (existing && !existing.icon && n && n.icon) existing.icon = n.icon;
    }

    const prev = groupSums.get(groupId) || { sentBytes: 0, receivedBytes: 0, totalBytes: 0, events: 0 };
    prev.sentBytes += Number(n.sentBytes) || 0;
    prev.receivedBytes += Number(n.receivedBytes) || 0;
    prev.totalBytes += Number(n.totalBytes) || 0;
    prev.events += Number(n.events) || 0;
    groupSums.set(groupId, prev);
  });

  groupMap.forEach((node, gid) => {
    const sums = groupSums.get(gid);
    if (!sums) return;
    node.sentBytes = sums.sentBytes;
    node.receivedBytes = sums.receivedBytes;
    node.totalBytes = sums.totalBytes;
    node.events = sums.events;
  });

  const linkMap = new Map();
  links.forEach((l) => {
    if (!l) return;
    const src = typeof l.source === 'object' ? l.source.id : l.source;
    const tgt = typeof l.target === 'object' ? l.target.id : l.target;
    if (!src || !tgt) return;

    const srcKey = nodeToGroup.get(String(src)) || `id:${normalizeTextKey(src)}`;
    const tgtKey = nodeToGroup.get(String(tgt)) || `id:${normalizeTextKey(tgt)}`;
    const srcNode = groupMap.get(srcKey);
    const tgtNode = groupMap.get(tgtKey);
    if (!srcNode || !tgtNode) return;

    const dir = (l.pcapDir || 'both').toString().toLowerCase();
    const v = Number(l.value) || 0;
    const p = Number(l.packets) || 0;
    const d = Number(l.duration) || 0;

    const ordered = [String(srcNode.id), String(tgtNode.id)].sort();
    const outSource = ordered[0];
    const outTarget = ordered[1];
    if (!outSource || !outTarget || outSource === outTarget) return;
    const key = `${outSource}↔${outTarget}`;

    const txPart = (dir === 'tx') ? v : (Number(l.txBytes) || 0);
    const rxPart = (dir === 'rx') ? v : (Number(l.rxBytes) || 0);

    const prev = linkMap.get(key);
    if (prev) {
      prev.value += v;
      prev.packets = (prev.packets || 0) + p;
      prev.duration = Math.max(prev.duration || 0, d);
      prev.txBytes = (prev.txBytes || 0) + txPart;
      prev.rxBytes = (prev.rxBytes || 0) + rxPart;

      // Best-effort: preserve representative endpoint MACs across grouping.
      // (Grouped endpoints may represent multiple IPs, so we just keep the first seen.)
      const macA = safeMac(l.macA) || safeMac(l.fromMac);
      const macB = safeMac(l.macB) || safeMac(l.toMac);
      if (macA && !safeMac(prev.macA)) prev.macA = macA;
      if (macB && !safeMac(prev.macB)) prev.macB = macB;

      try {
        const flows = l && l.pcapFlows && typeof l.pcapFlows === 'object' ? l.pcapFlows : null;
        if (flows) {
          if (!prev.pcapFlows) prev.pcapFlows = {};
          Object.keys(flows).forEach((k2) => {
            const f = flows[k2];
            if (!f) return;
            const existing = prev.pcapFlows[k2] || { clientPort: '-', serverPort: '-', clientBytes: 0, serverBytes: 0, packets: 0 };
            if (f.clientPort != null) existing.clientPort = f.clientPort;
            if (f.serverPort != null) existing.serverPort = f.serverPort;
            existing.clientBytes += Number(f.clientBytes) || 0;
            existing.serverBytes += Number(f.serverBytes) || 0;
            existing.packets += Number(f.packets) || 0;
            prev.pcapFlows[k2] = existing;
          });
        }
        if (!prev.ports && l && l.ports) prev.ports = l.ports;
      } catch (e) {
      }
    } else {
      const groupedLink = {
        id: key,
        source: outSource,
        target: outTarget,
        value: v,
        gas: 0,
        packets: p,
        duration: d,
        txBytes: txPart,
        rxBytes: rxPart,
        metaType: 'pcap',
        curvature: 0,
        pcapDir: 'both',
      };

      const macA = safeMac(l.macA) || safeMac(l.fromMac);
      const macB = safeMac(l.macB) || safeMac(l.toMac);
      if (macA) groupedLink.macA = macA;
      if (macB) groupedLink.macB = macB;

      try {
        const flows = l && l.pcapFlows && typeof l.pcapFlows === 'object' ? l.pcapFlows : null;
        if (flows) {
          groupedLink.pcapFlows = {};
          Object.keys(flows).forEach((k2) => {
            const f = flows[k2];
            if (!f) return;
            groupedLink.pcapFlows[k2] = {
              clientPort: f.clientPort,
              serverPort: f.serverPort,
              clientBytes: Number(f.clientBytes) || 0,
              serverBytes: Number(f.serverBytes) || 0,
              packets: Number(f.packets) || 0,
            };
          });
        }
        if (l && l.ports) groupedLink.ports = l.ports;
      } catch (e) {
      }

      linkMap.set(key, groupedLink);
    }
  });

  return {
    nodes: Array.from(groupMap.values()),
    links: Array.from(linkMap.values()),
  };
}

export function applyPcapLinkStyle(style, baseGraph) {
  const base = baseGraph || { nodes: [], links: [] };
  if (style === 'collapsed_straight') return buildPcapCollapsedGraph(base);
  return base;
}
